/**
 * Guide engine v3 overlay - engine strings (#1025, epic #1020). Spec R9 / R12.
 *
 * Engine-chrome copy (button fallbacks, cue fallback, error text, aria labels) is
 * NOT part of a package - package copy (heading / body / actionHint / button text)
 * is translated at publish. These are the engine's own strings. #1026 wires the
 * locale fallback chain (locale -> en-US -> key); here English is the default and
 * any caller may pass a translated `strings` map. Keeping the seam now means the
 * overlay never hardcodes a user-facing label inline.
 */

import { EXIT } from '../contract';
import { HIGHLIGHT_TIMEOUT_REASON } from '../runtime/GuideRuntimeContext.jsx';

/** Host-level error reasons (R11): the chip covers load + sandbox failures too. */
export const LOAD_ERROR_REASON = 'load-error';
export const SANDBOX_ERROR_REASON = 'sandbox-error';

export const DEFAULT_GUIDE_STRINGS = {
  exit: 'Exit',
  next: 'Next',
  done: 'Done',
  cueFallback: 'Complete the highlighted action to continue.',
  errorHighlightTimeout: 'This step could not load on the current screen. You can exit the guide.',
  errorLoad: "Couldn't load this guide. You can exit.",
  errorSandbox: "Couldn't set up the practice space. You can exit.",
  errorGeneric: 'Something went wrong with the guide. You can exit.',
  preparing: 'Setting up your practice space...',
  ariaDialog: 'Guide step',
  ariaExit: 'Exit guide',
  ariaReplay: 'Replay narration',
};

/**
 * Resolve a manual button's label: the authored (translated) `text`, else the
 * forward-button default - Done for the `_exit`-bound button, Next otherwise (R4).
 * @param {{text:(string|null), next:string}} button
 * @param {object} strings
 * @returns {string}
 */
export const manualLabel = (button, strings = DEFAULT_GUIDE_STRINGS) => {
  if (button && button.text) return button.text;
  return button && button.next === EXIT ? strings.done : strings.next;
};

/**
 * Error-chip message for a runtime error reason (R11).
 * @param {string|null} reason
 * @param {object} strings
 * @returns {string}
 */
export const errorMessage = (reason, strings = DEFAULT_GUIDE_STRINGS) => {
  if (reason === HIGHLIGHT_TIMEOUT_REASON) return strings.errorHighlightTimeout;
  if (reason === LOAD_ERROR_REASON) return strings.errorLoad;
  if (reason === SANDBOX_ERROR_REASON) return strings.errorSandbox;
  return strings.errorGeneric;
};
